import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import { readAttribution } from '../utils/attribution';
import { PACKAGES } from './WebsitesAdPage';
import './WebsitesAdPage.css';
import './CallBookingPage.css';

// Facebook ad landing pages: the only way to become a lead here is to book a
// 15-minute curiosity call. noindex, and absent from nav, sitemap and links.
// The booking API lives on audit.ubotika.site (booking.js in audit-webhook):
// a request holds the slot and emails a confirm link; the click books it and
// redirects back here with ?booked=1.
const API = 'https://audit.ubotika.site/book';
const TZ = 'America/Los_Angeles';

const PRODUCTS = {
  receptionist: {
    path: '/call/receptionist',
    seoTitle: 'Book a call: AI phone receptionist | UBOTIKA',
    kicker: 'AI phone receptionist',
    title: 'Every call answered, even when you can’t pick up.',
    points: [
      'Answers 24/7, books appointments, takes messages',
      'Texts you the details of every call',
    ],
    links: [
      { label: 'Try Tessa now: (424) 434-4541', href: 'tel:+14244344541' },
      { label: 'Talk to Edoe: (657) 835-0873', href: 'tel:+16578350873' },
    ],
  },
  website: {
    path: '/call/website',
    seoTitle: 'Book a call: business website | UBOTIKA',
    kicker: 'Websites for LA businesses',
    title: 'A real website for your business. $495.',
    points: [
      'Mobile-ready, with advanced visuals',
      'Contact form that texts you every lead',
    ],
    links: [
      { label: 'See advanced website features', href: '/showcase' },
      { label: 'Talk to Edoe: (657) 835-0873', href: 'tel:+16578350873' },
    ],
  },
  app: {
    path: '/call/app',
    seoTitle: 'Book a call: tailor-made app | UBOTIKA',
    kicker: 'Tailor-made apps',
    title: 'An app built for how your business works.',
    points: [
      'Your own CRM, built around how you sell',
      'Instant value checks and quotes for your customers',
      'A marketing bot that follows up for you',
      'A mobile app for your customers. You own the code.',
    ],
    links: [
      { label: 'See what we build', href: '/web-apps' },
      { label: 'Talk to Edoe: (657) 835-0873', href: 'tel:+16578350873' },
    ],
  },
};

const dayFmt = new Intl.DateTimeFormat('en-US', { timeZone: TZ, weekday: 'long', month: 'short', day: 'numeric' });
const timeFmt = new Intl.DateTimeFormat('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit' });

function track(event, params, eventID) {
  try {
    // eventID matches the server's Conversions API event so Meta counts the booking once.
    if (window.fbq) window.fbq(event.fb[0], event.fb[1], params, eventID ? { eventID } : undefined);
    if (window.gtag) window.gtag('event', event.ga, params);
  } catch (e) {
    /* tracking must never break booking */
  }
}

function Booker({ product }) {
  const [slots, setSlots] = useState(null);
  const [pick, setPick] = useState(null);
  const [day, setDay] = useState(0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState('idle');
  const [msg, setMsg] = useState('');
  const [sentTo, setSentTo] = useState('');

  useEffect(() => {
    fetch(`${API}/slots`).then((r) => r.json()).then((j) => setSlots(j.slots || []))
      .catch(() => setSlots([]));
  }, []);

  const days = useMemo(() => {
    const out = [];
    for (const s of slots || []) {
      const label = dayFmt.format(new Date(s));
      if (!out.length || out[out.length - 1].label !== label) out.push({ label, slots: [] });
      out[out.length - 1].slots.push(s);
    }
    return out;
  }, [slots]);

  const submit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    if (!name.trim()) { setMsg('Please enter your name.'); return; }
    if (!/^\S+@\S+\.\S+$/.test(email)) { setMsg('Please enter a valid email. The confirm link goes there.'); return; }
    if (phone.replace(/\D/g, '').length < 10) { setMsg('Please enter a phone number.'); return; }
    setStatus('sending'); setMsg('');
    try {
      const res = await fetch(`${API}/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ start: pick, name: name.trim(), email: email.trim(), phone: phone.trim(),
          product, page: window.location.pathname, utm: readAttribution() }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.ok) throw new Error(j.error || 'Could not hold that time.');
      track({ fb: ['trackCustom', 'BookingRequested'], ga: 'booking_requested' }, { product });
      setSentTo(j.email); setStatus('pending');
    } catch (err) {
      setStatus('idle'); setMsg(err.message);
    }
  };

  if (status === 'pending') {
    return (
      <div className="cb-card cb-done">
        <div className="cb-kicker">One last step</div>
        <h2>Check your email</h2>
        <p>We sent a confirm link to <strong>{sentTo}</strong>. Click it within 2 hours and the Zoom invite
          for {dayFmt.format(new Date(pick))} at {timeFmt.format(new Date(pick))} PT lands in your calendar.</p>
      </div>
    );
  }

  const current = days[day] || days[0];
  return (
    <form className="cb-card" onSubmit={submit} noValidate>
      <h2>Book a 15-minute call</h2>
      <p className="cb-sub">On Zoom. What you need, your timeline, and whether we’re a fit.</p>
      <label htmlFor="cb-name">Name</label>
      <input id="cb-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
      <label htmlFor="cb-phone">Phone</label>
      <input id="cb-phone" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
      <label htmlFor="cb-email">Email <span>(we send a confirm link here)</span></label>
      <input id="cb-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />

      <div className="cb-label">Pick a day and time <span>(Pacific)</span></div>
      {slots === null && <p className="cb-sub">Loading open times…</p>}
      {slots && !slots.length && <p className="cb-sub">No open times this week. Call (657) 835-0873.</p>}
      {days.length > 0 && (
        <>
          <div className="cb-days">
            {days.map((d, i) => (
              <button key={d.label} type="button" className={`cb-daybtn${i === day ? ' on' : ''}`}
                onClick={() => { setDay(i); setPick(null); }}>
                {d.label.replace(/,.*/, '').slice(0, 3)}<small>{d.label.replace(/^[^,]*, /, '')}</small>
              </button>
            ))}
          </div>
          <div className="cb-grid">
            {current.slots.map((s) => (
              <button key={s} type="button" className={`cb-slot${pick === s ? ' on' : ''}`} onClick={() => setPick(s)}>
                {timeFmt.format(new Date(s))}
              </button>
            ))}
          </div>
        </>
      )}
      {msg && <p className="cb-error">{msg}</p>}
      <button type="submit" className="cb-go" disabled={status === 'sending' || !pick}>
        {status === 'sending' ? 'Holding your time…'
          : pick ? `Book ${dayFmt.format(new Date(pick)).replace(/,.*/, '')} ${timeFmt.format(new Date(pick))}` : 'Pick a time above'}
      </button>
    </form>
  );
}

function CallBookingPage({ product }) {
  const p = PRODUCTS[product];
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const booked = params.get('booked') === '1';
  const when = params.get('when');

  useEffect(() => {
    // The confirm click lands here: this is the real conversion.
    if (booked) track({ fb: ['track', 'Schedule'], ga: 'booking_confirmed' }, { product }, params.get('eid') || undefined);
  }, [booked, product]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <SEO title={p.seoTitle} description={p.title} path={p.path} noindex nofollow />
      <header className="lpw-topbar">
        <span className="lpw-brand">
          <img src="/ubotika-logo-96.png" width="48" height="48" alt="" aria-hidden="true" />
          <span>UBOTIKA</span>
        </span>
      </header>
      <main id="main-content" className="lpw-page cb-page">
        {booked ? (
          <div className="cb-card cb-done">
            <div className="cb-kicker">You’re booked</div>
            <h2>{when || 'See you soon'}</h2>
            <p>The Zoom invite is in your email and calendar. Talk soon.</p>
          </div>
        ) : (
          <div className="cb-layout">
            <section className="cb-pitch cb-head">
              <div className="cb-kicker">{p.kicker}</div>
              <h1>{p.title}</h1>
              <ul>{p.points.map((x) => <li key={x}>{x}</li>)}</ul>
              <div className="cb-links">
                {p.links.map((l) => <a key={l.href} className="cb-link" href={l.href}>{l.label}</a>)}
              </div>
            </section>
            {product === 'website' && (
              <section className="cb-pitch cb-pricebox">
                <table className="lpw-table cb-prices">
                  <caption>Packages and prices</caption>
                  <tbody>
                    {PACKAGES.map((x) => (
                      <tr key={x.name}><th scope="row">{x.name}</th><td>{x.price}</td></tr>
                    ))}
                  </tbody>
                </table>
                <p className="cb-note">Domain, payment processing and business email are paid by you directly.</p>
              </section>
            )}
            <Booker product={product} />
          </div>
        )}
      </main>
      <footer className="lpw-footer">
        <span>&copy; {new Date().getFullYear()} UBOTIKA · Los Angeles, CA</span>
        <span>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms</Link>
        </span>
      </footer>
    </>
  );
}

export default CallBookingPage;
